import React, { useEffect, useMemo, useState } from 'react';
import { Wizard } from 'react-use-wizard';
import ProcessCard from './ProcessCard';
import { useTranslation } from 'react-i18next';
import { CellContext, Row } from '@tanstack/react-table';
import { TableColumnConfigs } from 'types';
import { Button } from 'components/Button';
import {
  DashboardHeader,
  ProcessLogButton,
  ProcessTableWrapper,
  TableFilterContainer
} from './ProcessTableWrapperWidget.elements';
import { RibbonComponent } from 'components';
import PencilAlt from 'icons/indexed-icons/PencilAlt';
import Save from 'icons/indexed-icons/Save';
import { DataLoader } from 'common/components/DataLoader/DataLoader';
import DraggableColumnOptionHeader from './DraggableColumnOptionHeader';
import { ToastMsg } from 'common/components/Toast';
import { convertToZonedTimestamp } from 'common/helpers/dateAndTimeHelpersV2';
import { useWidgetDateRange } from 'common/providers';
import { handleClick } from 'components/StyledUi/DownloadCSVButton/utils';
import Download from 'icons/indexed-icons/Download';
import { useMachineInfo } from 'common/pages/fleetV2/providers';

interface ProcessLogDetailModalProps {
  handleClose?: () => void;
  active?: boolean;
  isLoading?: boolean;
  processData: Record<string, unknown> | null | undefined;
  processOrder: Record<string, unknown> | null | undefined;
  machineId: string | undefined;
}

const processSteubenTitleMapping: Record<string, string> = {
  process0: 'HPV + AIR',
  process1: 'HPV + AVD',
  process2: 'Product',
  process3: 'External Cleaning',
  process4: 'Internal CIP',
  process5: 'NVD'
};

const processHeartlandTitleMapping: Record<string, string> = {
  'process0': 'HPV + AIR',
  'process1': 'HPV + AVD',
  'process2': 'Product',
  'process3': 'External Cleaning',
  'process4': 'Internal CIP',
  'process5': 'Aseptic Barrier',
  'process6': 'N2 Flush',
  'process8': 'Stirrer'
};

const processTitle = (machineId?: string) => {
  
  if (
      [
        'bbc03b5b-ea3f-49f9-a24b-b5ca3b023249',
        '28b2d3a9-2074-4c58-ac8a-a0f16575829b',
        '2a64b2ff-9894-4a1f-b8e6-cd4ae068c34b',
      ].includes(machineId || '')
    ) return processHeartlandTitleMapping;
    
  return processSteubenTitleMapping

}

const handleExportCSV = (processData?: Record<string, unknown>, timeZone?: string, machineId?: string): void => {
  const finalData = [];


  const currentProcess = processTitle(machineId)

  for (const i in processData) {
    const data = processData[i] as Record<string, unknown>[];

    const newData =
      (data || [])?.reduce((acc: Record<string, unknown>[], curr) => {
        const { logtime, processlogId, stepnr, stepdescreng, jumpnr, jumpdescreng } = curr;
        const item = {
          process: currentProcess[i],
          logtime: convertToZonedTimestamp(logtime as string, timeZone, 'MM/dd/yyyy - HH:mm:ss'),
          processlogId,
          stepnr,
          stepdescreng,
          jumpnr: jumpnr || null,
          jumpdescreng: jumpdescreng || null
        };
        acc.push(item);
        return acc;
      }, []) || [];

    finalData.push(...newData);
  }

  const headers = {
    process: 'Process',
    logtime: `Logtime`,
    processlogId: 'ID',
    stepnr: 'StepNR',
    stepdescreng: 'Description',
    jumpnr: 'JumpNr',
    jumpdescreng: 'Jump Description'
  };

  const fileName = 'Process Analysis.csv';

  handleClick?.(finalData, headers, fileName, timeZone);
};

const ProcessLogModal: React.FC<ProcessLogDetailModalProps> = ({
  active,
  handleClose,
  processData,
  isLoading,
  machineId
}) => {
  if (!active) return null; // Don't render modal content if not active
  const { t } = useTranslation(['mh']);
  const [processOrder, setProcessOrder] = useState<string[]>(
    processData ? Object.keys(processData) : []
  );
  const [isEditing, setIsEditing] = useState(false); // For Edit Mode
  const [initialOrder, setInitialOrder] = useState<string[]>([]); // To store initial order for undo
  const [columnState, setColumnState] = useState<string[]>([]);
  const [columnOrderState, setColumnOderState] = useState<string[]>([]);
  const { timeZone } = useWidgetDateRange();
  const { id } = useMachineInfo() as { id?: string; };

  type DynamicData = Record<string, unknown>;

  const columnsHeader = [
    {
      id: 'logtime',
      header: t('logtime'),
      isEnableSorting: true,
      isSelected: true,
      isEnableResizing: true,
      // isAlwaysVisible: true,
      renderer: ({ row }: { row: Row<DynamicData> }) => {
        const startTimestamp = row.original.logtime as string;
        const formattedStartTimestamp = convertToZonedTimestamp(
          startTimestamp as string,
          timeZone,
          'MM/dd/yyyy - HH:mm:ss'
        );

        return <>{formattedStartTimestamp}</>;
      }
    },
    {
      id: 'processlogId',
      header: t('id'),
      isEnableSorting: true,
      isEnableResizing: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => cellValue.getValue()
    },
    {
      id: 'stepnr',
      header: t('StepNr '),
      isEnableSorting: true,
      isSelected: true,
      isEnableResizing: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => cellValue.getValue()
    },
    {
      id: 'stepdescreng',
      header: t('description'),
      isEnableSorting: true,
      isEnableResizing: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => cellValue.getValue()
    },
    {
      id: 'jumpnr',
      header: t('jump_nr'),
      isEnableSorting: true,
      isEnableResizing: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const value = cellValue.getValue();
        return  value === undefined ? 'Null' : value; // Return `Null` if value is missing, or the data otherwise
      },
      sortUndefined: 'last'
    },
    {
      id: 'jumpdescreng',
      header: t('Jump Description'),
      isEnableSorting: true,
      isEnableResizing: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const value = cellValue.getValue();
        return value === undefined ? 'Null' : value; // Return `Null` if value is missing, or the data otherwise
      },
      sortUndefined: 'last'
    }
  ];

  const columnOptions = useMemo(() => columnsHeader, [])
    const tableColumnOptions = useMemo(() => {
    const enabledCol = columnsHeader.filter((column) => !columnState.includes(column.id))
    const colOrder = columnOrderState.length ? columnOrderState : enabledCol.map((col) => col.id)
    const newOrder = [] as typeof columnsHeader
    enabledCol.forEach((col) => {
      const colIndex = colOrder.findIndex((colName) => colName === col.id);
      newOrder[colIndex] = col
    })
    return newOrder
  }, [columnState, columnOrderState]) 

  const defaultSortState = [{
    id: 'processlogId',
    desc: true
  }];
 
  
  const handleDrag = (draggedKey: string, hoverKey: string) => {
    if (!isEditing) return; // Prevent dragging if not in Edit mode
    const newOrder = [...processOrder];
    const draggedIndex = newOrder.indexOf(draggedKey);
    const hoverIndex = newOrder.indexOf(hoverKey);

    if (draggedIndex !== -1 && hoverIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(hoverIndex, 0, draggedKey);
      setProcessOrder(newOrder);
    }
  };

  
  const saveChanges = () => {
    // Saving order in Session Storage
    if (isEditing) {
      sessionStorage.setItem('processOrder', JSON.stringify(processOrder));
      ToastMsg({
        heading: t('Saved Order') || '',
        type: 'success',
        message: t('Order has been saved.'),
        position: 'top-right'
      });
    }
  };

  const handleToggleEditing = () => {
    isEditing && saveChanges(); // Saving only if we're currently editing else skip
    setIsEditing(!isEditing);   // Then toggle the editing state
  };

  const ribbonItems = useMemo(
    () => [
      {
        label: isEditing ? t('Save Layout') : t('Edit Layout'),
        icon: () => (isEditing ? <Save /> : <PencilAlt />),
        onClick: () => handleToggleEditing()
      },
      {
        label: 'Export to .CSV',
        icon: () => <Download />,
        onClick: () => handleExportCSV(processData ?? undefined, timeZone, machineId)
      }
    ],
    [t, isEditing, initialOrder, processOrder]
  );
  
  useEffect(() => {
    if (processData) {
      // Load process order from session storage if available
      const savedOrder = sessionStorage.getItem('processOrder');
      const order = savedOrder ? JSON.parse(savedOrder) : Object.keys(processData);
      setProcessOrder(order);
      setInitialOrder(order); // Store initial order for undo
    }
  }, [processData]);

  if (!processData || processOrder.length === 0) {
    return <DataLoader isLoading={isLoading} />;
  }

  const tableData = Array.isArray(processData)
    ? processData
    : Object.entries(processData).map(([key, value]) => ({
        key,
        ...(typeof value === 'object' && value !== null ? value : {}) // To make TS happy :)
      }));

  function changeColumn(name: string | string[]){
    
    if(Array.isArray(name)){
      setColumnState(name)
      return
    }
    if(!columnState.includes(name)) setColumnState([...columnState, name])
    else {
      const newColumn = columnState.filter((col) => col !== name)
      setColumnState(newColumn)
    }  
  }

  const getProcessLogColumnOrder = (column: string[]) => {
    setColumnOderState(column)
  }

  return (
    <>
      {active && (
        <Wizard>
          <div className="dashboard">
            <DashboardHeader>
              <h2 className="dashboard-header">Process Analysis</h2>
              <div className="dashboard-options">
                <TableFilterContainer>
                  <RibbonComponent ribbonItems={ribbonItems} />
                  <DraggableColumnOptionHeader
                    columnConfigs={columnOptions}
                    newTableData={tableData}
                    sortState={defaultSortState}
                    changeColumn={changeColumn}
                    getProcessLogColumnOrder={getProcessLogColumnOrder}
                  />
                </TableFilterContainer>
              </div>
            </DashboardHeader>
            <div
              className="process-grid"
              style={{ backgroundColor: isEditing ? '#F2F3F4' : 'white' }}
            >
              <ProcessTableWrapper className="process-log">
                {processOrder.map((processKey) => (
                  <ProcessCard
                    key={processKey}
                    processKey={processKey}
                    processData={processData[processKey] as Record<string, unknown>} 
                    onDrag={handleDrag}
                    columns={tableColumnOptions}
                    isLoading={isLoading}
                    defaultSortState={defaultSortState}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    machineId={machineId}
                    timeZone={timeZone}
                  />
                ))}
              </ProcessTableWrapper>
            </div>
            <ProcessLogButton>
              <Button className="close-btn" variant="filled-primary" onClick={handleClose}>
                Close
              </Button>
            </ProcessLogButton>
          </div>
        </Wizard>
      )}
    </>
  );
};

export default ProcessLogModal;
